// Inline name editing.
//
// A name cell renders a link (the "display" target) next to a pencil
// button. Clicking the pencil swaps the link for a text input; Enter or
// blur saves via PATCH, Escape cancels. On a successful save the page
// reloads so the server-rendered value is fresh. A plain click on the
// link just navigates — an edit only ever starts from the pencil, so a
// stray click can never trip into edit mode.
import { Controller } from "@hotwired/stimulus"

const INPUT_CLS =
  "min-w-0 flex-1 rounded border border-[var(--color-brand-500)] bg-black/40 px-1.5 py-0.5 text-sm text-[var(--color-ink)] outline-none focus:shadow-[0_0_0_2px_var(--color-brand-ring)]"

export default class extends Controller<HTMLElement> {
  static targets = ["display", "pencil"]
  static values = { url: String, param: String }
  declare readonly displayTarget: HTMLElement
  declare readonly pencilTarget: HTMLElement
  declare readonly urlValue: string
  declare readonly paramValue: string

  private input: HTMLInputElement | null = null

  edit(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    if (this.input) return

    const original = this.displayTarget.textContent?.trim() ?? ""
    const input = document.createElement("input")
    input.type = "text"
    input.value = original
    input.className = INPUT_CLS
    this.input = input

    this.displayTarget.style.display = "none"
    this.pencilTarget.style.display = "none"
    this.displayTarget.after(input)
    input.focus()
    input.select()

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault()
        this.save(original)
      } else if (e.key === "Escape") {
        e.preventDefault()
        this.restore()
      }
    })
    input.addEventListener("blur", () => this.save(original))
  }

  private async save(original: string): Promise<void> {
    const input = this.input
    if (!input) return
    const value = input.value.trim()
    if (value === "" || value === original) {
      this.restore()
      return
    }
    // Commit: detach now so the blur that fires when we disable the
    // input is a no-op rather than a second save.
    this.input = null
    input.disabled = true

    const csrf =
      (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null)?.content ?? ""
    const fd = new FormData()
    fd.append(this.paramValue, value)
    fd.append("_method", "patch")
    fd.append("authenticity_token", csrf)

    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html, application/json" },
      })
      if (res.ok || res.redirected) {
        window.location.reload()
        return
      }
    } catch {
      /* fall through to restore */
    }
    this.input = input
    this.restore()
  }

  private restore(): void {
    const input = this.input
    if (!input) return
    this.input = null
    input.remove()
    this.displayTarget.style.display = ""
    this.pencilTarget.style.display = ""
  }
}
